import { defineAsyncComponent } from '../../../lib/utils/vue3compat/vue.js?vue3';
import { GlAlert, GlIcon, GlLink, GlLoadingIcon, GlSprintf, GlTooltipDirective } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import DuoWorkflowAction from '../../../../../../ee/app/assets/javascripts/ai/shared/widgets/duo_workflow_action.vue?vue3';
import { timeIntervalInWords } from '~/lib/utils/datetime_utility';
import { setUrlFragment, visitUrl } from '~/lib/utils/url_utility';
import { __, s__, n__, sprintf, formatNumber } from '~/locale';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import PageHeading from '~/vue_shared/components/page_heading.vue';
import PanelActionsPortal from '../../../vue_shared/components/panel_actions_portal.vue?vue3';
import { buildFixPipelineContext, reportToSentry } from '~/ci/utils';
import ClipboardButton from '../../../vue_shared/components/clipboard_button.vue?vue3';
import CiIcon from '../../../vue_shared/components/ci_icon/ci_icon.vue?vue3';
import TimeAgoTooltip from '../../../vue_shared/components/time_ago_tooltip.vue?vue3';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { FIX_PIPELINE_AGENT_PRIVILEGES } from '~/duo_agent_platform/constants';
import { setFaviconOverlay, resetFavicon } from '~/lib/utils/favicon';
import { InternalEvents } from '../../../tracking/index.js?vue3';
import { LOAD_FAILURE, POST_FAILURE, DELETE_FAILURE, DEFAULT } from '../constants';
import cancelPipelineMutation from '../graphql/mutations/cancel_pipeline.mutation.graphql';
import deletePipelineMutation from '../graphql/mutations/delete_pipeline.mutation.graphql';
import retryPipelineMutation from '../graphql/mutations/retry_pipeline.mutation.graphql';
import { getQueryHeaders } from '../graph/utils.js?vue3';
import pipelineHeaderStatusUpdatedSubscription from './graphql/subscriptions/pipeline_header_status_updated.subscription.graphql';
import { MERGE_TRAIN_EVENT_TYPE } from './constants';
import HeaderActions from './components/header_actions.vue?vue3';
import HeaderBadges from './components/header_badges.vue?vue3';
import getPipelineQuery from './graphql/queries/get_pipeline_header_data.query.graphql';
const finishedStatuses = [
    'FAILED',
    'SUCCESS',
    'CANCELED'
];
const pipelineCancel = 'pipelineCancel';
const pipelineRetry = 'pipelineRetry';
const errorTexts = {
    [LOAD_FAILURE]: __('We are currently unable to fetch data for the pipeline header.'),
    [POST_FAILURE]: __('An error occurred while making the request.'),
    [DELETE_FAILURE]: __('An error occurred while deleting the pipeline.'),
    [DEFAULT]: __('An unknown error occurred.')
};
export default {
    name: 'PipelineHeader',
    components: {
        CiIcon,
        ClipboardButton,
        GlAlert,
        GlIcon,
        GlLink,
        GlLoadingIcon,
        GlSprintf,
        PageHeading,
        PanelActionsPortal,
        HeaderActions,
        HeaderBadges,
        TimeAgoTooltip,
        DuoWorkflowAction,
        PipelineAgentSessions: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/ci/pipeline_details/header/components/pipeline_agent_sessions.vue?vue3')),
        PipelineAccountVerificationAlert: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/vue_shared/components/pipeline_account_verification_alert.vue?vue3')),
        HeaderMergeTrainsLink: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/ci/pipeline_details/header/components/header_merge_trains_link.vue?vue3'))
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        SafeHtml
    },
    mixins: [
        InternalEvents.mixin()
    ],
    inject: {
        graphqlResourceEtag: {
            default: ''
        },
        paths: {
            default: {}
        },
        pipelineIid: {
            default: ''
        }
    },
    apollo: {
        pipeline: {
            context () {
                return {
                    ...getQueryHeaders(this.graphqlResourceEtag),
                    featureCategory: 'continuous_integration'
                };
            },
            query: getPipelineQuery,
            variables () {
                return {
                    fullPath: this.paths.fullProject,
                    iid: this.pipelineIid
                };
            },
            skip () {
                return !this.pipelineIid;
            },
            update (data) {
                const pipeline = data?.project?.pipeline;
                if (!pipeline) {
                    return this.pipeline;
                }
                this.setFavicon(pipeline.detailedStatus?.favicon);
                return pipeline;
            },
            result ({ data }) {
                // we use a manual subscribeToMore call due to issues with
                // the skip hook not working correctly for the subscription
                if (data?.project?.pipeline?.id && !this.isSubscribed) {
                    this.isSubscribed = true;
                    this.$apollo.queries.pipeline.subscribeToMore({
                        document: pipelineHeaderStatusUpdatedSubscription,
                        variables: {
                            pipelineId: data.project.pipeline.id
                        },
                        updateQuery (previousData, { subscriptionData: { data: { ciPipelineStatusUpdated } } }) {
                            if (ciPipelineStatusUpdated) {
                                return {
                                    project: {
                                        ...previousData.project,
                                        pipeline: {
                                            ...previousData.project.pipeline,
                                            ...ciPipelineStatusUpdated
                                        }
                                    }
                                };
                            }
                            return previousData;
                        }
                    });
                }
            },
            error (error) {
                this.reportFailure(LOAD_FAILURE);
                reportToSentry(this.$options.name, error);
            },
            watchLoading (isLoading) {
                if (!isLoading) {
                    // To ensure apollo has updated the cache,
                    // we only remove the loading state in sync with GraphQL
                    this.isCanceling = false;
                    this.isRetrying = false;
                }
            }
        }
    },
    data () {
        return {
            pipeline: null,
            failureMessages: [],
            failureType: null,
            isCanceling: false,
            isRetrying: false,
            isDeleting: false,
            isSubscribed: false,
            favicon: null
        };
    },
    computed: {
        loading () {
            return this.$apollo.queries.pipeline.loading;
        },
        hasError () {
            return this.failureType;
        },
        detailedStatus () {
            return this.pipeline?.detailedStatus || {};
        },
        status () {
            return this.pipeline?.status;
        },
        isFinished () {
            return finishedStatuses.includes(this.status);
        },
        failure () {
            switch(this.failureType){
                case LOAD_FAILURE:
                    return {
                        text: errorTexts[LOAD_FAILURE],
                        variant: 'danger'
                    };
                case POST_FAILURE:
                    return {
                        text: errorTexts[POST_FAILURE],
                        variant: 'danger'
                    };
                case DELETE_FAILURE:
                    return {
                        text: errorTexts[DELETE_FAILURE],
                        variant: 'danger'
                    };
                default:
                    return {
                        text: errorTexts[DEFAULT],
                        variant: 'danger'
                    };
            }
        },
        pipelineId () {
            const id = getIdFromGraphQLId(this.pipeline?.id);
            if (id) {
                return `#${id}`;
            }
            return '';
        },
        user () {
            return this.pipeline?.user;
        },
        userId () {
            return getIdFromGraphQLId(this.user?.id);
        },
        shortId () {
            return this.pipeline?.commit?.shortId || '';
        },
        commitSha () {
            return this.pipeline?.commit?.sha || '';
        },
        commitPath () {
            return this.pipeline?.commit?.webPath || '';
        },
        commitTitle () {
            return this.pipeline?.commit?.title || this.pipeline?.commitTitle || '';
        },
        totalJobsText () {
            const totalJobs = this.pipeline?.totalJobs || 0;
            return sprintf(n__('%{jobs} job', '%{jobs} jobs', totalJobs), {
                jobs: totalJobs
            });
        },
        createdAt () {
            return this.pipeline?.createdAt;
        },
        finishedAt () {
            return this.pipeline?.finishedAt;
        },
        createdDetailsText () {
            // It's possible for some/all to be empty (including `createdAt`, see: https://gitlab.com/gitlab-org/gitlab/-/issues/25795)
            if (this.user) {
                if (this.createdAt && this.finishedAt) {
                    return s__('Pipeline|Created %{createdTimeAgo} by %{user}, finished %{finishedTimeAgo}');
                }
                if (this.createdAt) {
                    return s__('Pipeline|Created %{createdTimeAgo} by %{user}');
                }
                if (this.finishedAt) {
                    return s__('Pipeline|Created by %{user}, finished %{finishedTimeAgo}');
                }
                return s__('Pipeline|Created by %{user}');
            }
            if (this.createdAt && this.finishedAt) {
                return s__('Pipeline|Created %{createdTimeAgo}, finished %{finishedTimeAgo}');
            }
            if (this.createdAt) {
                return s__('Pipeline|Created %{createdTimeAgo}');
            }
            if (this.finishedAt) {
                return s__('Pipeline|Finished %{finishedTimeAgo}');
            }
            return '';
        },
        inProgress () {
            return this.status === 'RUNNING';
        },
        duration () {
            return this.pipeline?.duration || 0;
        },
        showDuration () {
            return this.duration && this.isFinished;
        },
        durationFormatted () {
            return timeIntervalInWords(this.duration);
        },
        queuedDuration () {
            return this.pipeline?.queuedDuration || 0;
        },
        inProgressText () {
            return sprintf(__('In progress, queued for %{queuedDuration} seconds'), {
                queuedDuration: formatNumber(this.queuedDuration)
            });
        },
        durationText () {
            return sprintf(__('%{duration}, queued for %{queuedDuration} seconds'), {
                duration: this.durationFormatted,
                queuedDuration: formatNumber(this.queuedDuration)
            });
        },
        computeMinutes () {
            return this.pipeline?.computeMinutes;
        },
        showComputeMinutes () {
            return this.isFinished && this.computeMinutes;
        },
        pipelineName () {
            return this.pipeline?.name;
        },
        refText () {
            return this.pipeline?.refText;
        },
        isMergeTrainPipeline () {
            return this.pipeline?.mergeRequestEventType === MERGE_TRAIN_EVENT_TYPE;
        },
        isFailed () {
            return this.status === 'FAILED';
        },
        pipelinePath () {
            return this.pipeline?.detailedStatus?.detailsPath ? `${gon.gitlab_url}${this.pipeline.detailedStatus.detailsPath}` : null;
        },
        sourceBranch () {
            return this.pipeline?.mergeRequest?.sourceBranch || this.pipeline?.ref || null;
        },
        showFixPipelineButton () {
            return this.isFailed && this.pipelinePath && this.sourceBranch;
        },
        getAdditionalContext () {
            return buildFixPipelineContext({
                source: this.pipeline?.source,
                sourceBranch: this.sourceBranch,
                mergeRequestPath: this.paths.mergeRequestPath
            });
        }
    },
    beforeDestroy () {
        resetFavicon();
    },
    methods: {
        setFavicon (newFavicon) {
            if (newFavicon && this.favicon !== newFavicon) {
                setFaviconOverlay(newFavicon);
                this.favicon = newFavicon;
            }
        },
        trackHeaderAction (event) {
            this.trackEvent(event, {
                label: this.pipeline?.source
            });
        },
        reportFailure (errorType, errorMessages = []) {
            this.failureType = errorType;
            this.failureMessages = errorMessages;
        },
        async postPipelineAction (name, mutation, id) {
            try {
                const { data: { [name]: { errors } } } = await this.$apollo.mutate({
                    mutation,
                    variables: {
                        id
                    }
                });
                if (errors.length > 0) {
                    this.isRetrying = false;
                    this.reportFailure(POST_FAILURE, errors);
                } else {
                    await this.$apollo.queries.pipeline.refetch();
                }
            } catch (error) {
                this.isRetrying = false;
                this.reportFailure(POST_FAILURE);
                reportToSentry(this.$options.name, error);
            }
        },
        cancelPipeline (id) {
            this.isCanceling = true;
            this.postPipelineAction(pipelineCancel, cancelPipelineMutation, id);
        },
        retryPipeline (id) {
            this.trackHeaderAction('click_retry_pipeline_in_pipeline_header');
            this.isRetrying = true;
            this.postPipelineAction(pipelineRetry, retryPipelineMutation, id);
        },
        async deletePipeline (id) {
            this.trackHeaderAction('click_delete_pipeline_in_pipeline_header');
            this.isDeleting = true;
            try {
                const { data: { pipelineDestroy: { errors } } } = await this.$apollo.mutate({
                    mutation: deletePipelineMutation,
                    variables: {
                        id
                    }
                });
                if (errors.length > 0) {
                    this.reportFailure(DELETE_FAILURE, errors);
                    this.isDeleting = false;
                } else {
                    visitUrl(setUrlFragment(this.paths.pipelinesPath, 'delete_success'));
                }
            } catch (error) {
                this.isDeleting = false;
                this.reportFailure(DELETE_FAILURE);
                reportToSentry(this.$options.name, error);
            }
        }
    },
    FIX_PIPELINE_AGENT_PRIVILEGES,
    SOURCE: 'fix_pipeline'
};
